import { Button } from "@components/common/button/button";
import { Chip } from "@components/common/chip/chip";
import { ContentTransition, PageStatus } from "@components/common/content-transition/content-transition";
import { useTheme } from "@components/common/hooks/theme";
import { Add } from "@components/common/icons/add";
import { AddCircle } from "@components/common/icons/add-circle";
import { ArrowLeft } from "@components/common/icons/arrow-left";
import { Business } from "@components/common/icons/business";
import { Check } from "@components/common/icons/check";
import { Notifications } from "@components/common/icons/notifications";
import { OpenLock } from "@components/common/icons/open-lock";
import { Person } from "@components/common/icons/person";
import { Settings } from "@components/common/icons/settings";
import { Modal } from "@components/common/modal/modal";
import { Option } from "@components/common/option/option";
import { OptionGroup } from "@components/common/option/option-group/option-group";
import { Popover } from "@components/common/popover/popover";
import BaseProfileForm, { BaseProfileFormMode } from "@components/companies/forms/new-profile-form";
import { ThemeSwitch } from "@components/layout/theme-switch/theme-switch";
import { NetworkConnector } from "@components/network-connector/network-connector";
import { handleLogOut, refreshUserData } from "@helper/auth/auth-api-helper";
import AuthStore from "@helper/auth/auth-store";
import { getUserCompanies, isBusinessProfile } from "@helper/auth/companies-helper";
import { IProfileType } from "@models/session/current-user";
import { VIRTUAL_TOUR_STEPS } from "constants/virtual-tour";
import { activeCompany } from "helper/companies/companies-api-helper";
import Router, { useRouter } from "next/router";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

const Navbar: FC = () => {
    const { t } = useTranslation();
    const router = useRouter();
    const { mode } = useTheme();
    const [loadingLogout, setLoadingLogout] = useState<boolean>(false);
    const [loadingSwitchAccount, setLoadingSwitchAccount] = useState<boolean>(false);
    const [networkConnectorModalOpen, setNetworkConnectorModalOpen] = useState<boolean>(false);
    const [createBusinessProfileModalOpen, setCreateBusinessProfileModalOpen] = useState<boolean>(false);
    const user = AuthStore((state) => state.user);
    const isAdmin = user?.role === "admin";
    const currentCompanySlug = user?.company_slug;
    const defaultPageStatus: PageStatus = {
        pageKey: 0,
        direction: "next",
    };
    const [pageStatus, setPageStatus] = useState<PageStatus>(defaultPageStatus);
    const isBusiness = isBusinessProfile();
    const companies = getUserCompanies();
    const [selectingSlug, setSelectingSlug] = useState<string>("");

    const onLogout = () => {
        setLoadingLogout(true);
        const doA = async () => {
            const data = await handleLogOut();
            if (data) {
                setLoadingLogout(false);
                Router.push("/");
            }
        };
        doA();
    };

    const onSwitchCompany = async (companySlug: string, profileType: IProfileType) => {
        try {
            setSelectingSlug(companySlug);
            setLoadingSwitchAccount(true);
            const newProfile = await activeCompany(companySlug, profileType);
            await refreshUserData();
            const isBusinessProfile = newProfile?.profileType === "business" || newProfile.slug;
            if (isBusinessProfile) {
                await router.push(`/companies/${newProfile.slug}/profile/info`);
            } else {
                await router.push(`/profile/info`);
            }
        } catch (error) {
            toast.error("Failed to switch company");
        } finally {
            setLoadingSwitchAccount(false);
        }
    };

    return (
        <>
            <ThemeSwitch />
            <div className="ml-2 h-4 w-px bg-gray-200" />
            {!isAdmin && (
                <>
                    <Button
                        leftAdornments={
                            <div id={VIRTUAL_TOUR_STEPS.NAVBAR_SOCIALS_ICON.target}>
                                <AddCircle />
                            </div>
                        }
                        onClick={() => setNetworkConnectorModalOpen(true)}
                        rounded="full"
                        color="white"
                        focusState="hover"
                        tooltip={{ text: t("topbar.networkConnector", { defaultValue: "Network connector" }), placement: "bottom-center" }}
                    />
                    <Button
                        leftAdornments={<Notifications />}
                        rounded="full"
                        color="white"
                        focusState="hover"
                        link={{ href: "/reminder" }}
                        tooltip={{ text: t("topbar.mediaReminder", { defaultValue: "Media reminder" }), placement: "bottom-center" }}
                    />
                </>
            )}
            <Button
                leftAdornments={<Settings />}
                rounded="full"
                color="white"
                focusState="hover"
                link={{ href: "/settings" }}
                tooltip={{ text: t("topbar.settings", { defaultValue: "Settings" }), placement: "bottom-center" }}
            />
            <Popover
                button={<Button leftAdornments={<Person />} rounded="full" color="white" focusState="hover" />}
                onToggle={(open) => {
                    if (!open) {
                        setPageStatus(defaultPageStatus);
                    }
                }}
            >
                <ContentTransition pageStatus={pageStatus}>
                    <ContentTransition.Page key={0}>
                        <Option
                            label={user?.name && user?.lastName ? `${user.name} ${user.lastName}` : user?.username || "Unknown username"}
                            leftAdornments={<Person />}
                            rounded="lg"
                            link={{ href: "/profile/info" }}
                        />
                        <Option
                            label={t("topbar.settings", { defaultValue: "Settings" })}
                            leftAdornments={<Settings />}
                            rounded="lg"
                            link={{ href: "/settings" }}
                        />
                        <Option divider={true} />
                        <Option
                            label={t("topbar.createBusinessProfile", { defaultValue: "Create a business profile" })}
                            leftAdornments={<Add />}
                            rounded="lg"
                            onClick={() => setCreateBusinessProfileModalOpen(true)}
                        />
                        <Option divider={true} />
                        <Option
                            label={t("topbar.logout", { defaultValue: "Sign out" })}
                            leftAdornments={<OpenLock />}
                            color={mode === "dark" ? "primary" : "red"}
                            onClick={onLogout}
                            loading={loadingLogout}
                            rounded="lg"
                        />
                        <Option divider={true} />
                        <Button
                            label={t("topbar.showAllProfiles", { defaultValue: "Show all profiles" })}
                            rounded="lg"
                            color="primary"
                            block={true}
                            onClick={() =>
                                setPageStatus({
                                    pageKey: 1,
                                    direction: "next",
                                })
                            }
                        />
                    </ContentTransition.Page>
                    <ContentTransition.Page key={1}>
                        <Option
                            label={t("topbar.back", { defaultValue: "Back" })}
                            leftAdornments={<ArrowLeft />}
                            rounded="lg"
                            onClick={() =>
                                setPageStatus({
                                    pageKey: 0,
                                    direction: "prev",
                                })
                            }
                        />
                        <Option divider={true} />
                        <Option
                            label={t("topbar.personalProfile", { defaultValue: "Personal profile" })}
                            leftAdornments={<Person />}
                            rightAdornments={!isBusiness ? <Chip leftAdornments={<Check />} size="xs" rounded="full" color="green" /> : undefined}
                            onClick={() => onSwitchCompany(null, "personal")}
                            loading={loadingSwitchAccount && !isBusiness}
                            rounded="lg"
                        />
                        {companies.length > 0 && (
                            <OptionGroup label={t("topbar.businessProfiles", { defaultValue: "Business profiles" })}>
                                {companies.map((company, index) => (
                                    <Option
                                        label={company.name}
                                        leftAdornments={<Business />}
                                        rightAdornments={
                                            currentCompanySlug === company.slug ? (
                                                <Chip leftAdornments={<Check />} size="xs" rounded="full" color="green" />
                                            ) : undefined
                                        }
                                        onClick={() => onSwitchCompany(company.slug, "business")}
                                        loading={loadingSwitchAccount && selectingSlug === company.slug}
                                        rounded="lg"
                                        key={index}
                                    />
                                ))}
                            </OptionGroup>
                        )}
                    </ContentTransition.Page>
                </ContentTransition>
            </Popover>
            <Modal
                open={networkConnectorModalOpen}
                onClose={() => setNetworkConnectorModalOpen(false)}
                rounded="lg"
                width="700px"
                title={t("topbar.networkConnector.title", { defaultValue: "Network connector" })}
                subtitle={t("topbar.networkConnector.subtitle", {
                    defaultValue: "Connect your favorite networks to seamlessly access and enjoy their content.",
                })}
            >
                <NetworkConnector />
            </Modal>
            <Modal
                open={createBusinessProfileModalOpen}
                onClose={() => setCreateBusinessProfileModalOpen(false)}
                rounded="lg"
                title={t("topbar.createBusinessProfile", { defaultValue: "Create a business profile" })}
            >
                <BaseProfileForm mode={BaseProfileFormMode.ADD} />
            </Modal>
        </>
    );
};
export default Navbar;
